/**
 * Utility for building searchable channel list combining regular, local, and RSN channels
 */

import { denormalizeChannels } from "lib/helper/channel-interactive-tool-v2";
import { localLogoFields } from "./local-rsn-utils";

interface LocalRsnChannelsData {
  localChannels: any[];
  rsnChannels: any[];
}

/**
 * Builds a searchable channel list by combining:
 * 1. Regular channels from chlupV2 (denormalized if needed)
 * 2. Local channels from zipcode API
 * 3. RSN channels from zipcode API
 *
 * @param allChannels - All channels from chlupV2 (can be string[] or object[])
 * @param channelMap - Map of channel IDs to channel objects
 * @param localRsnChnlsData - Local and RSN channels from zipcode API
 * @returns Array of searchable channel objects with displayText property
 */
export const buildSearchableChannels = (allChannels?: any[] | string[], channelMap?: Record<string, any>, localRsnChnlsData?: LocalRsnChannelsData): any[] => {
  let channels: any[] = [];

  // Add regular channels from chlupV2
  if (allChannels && channelMap) {
    // Check if allChannels is already denormalized (array of objects) or normalized (array of strings)
    const firstChannel = allChannels[0];
    if (typeof firstChannel === "string") {
      // Denormalize: string[] -> ChannelCardV2[]
      channels = denormalizeChannels(allChannels as string[], channelMap);
    } else {
      // Already denormalized
      channels = allChannels as any[];
    }
  }

  // Add local/RSN channels with displayText for search
  if (localRsnChnlsData && (localRsnChnlsData.localChannels.length > 0 || localRsnChnlsData.rsnChannels.length > 0)) {
    // Transform local channels to include displayText
    const localChannelsForSearch = localRsnChnlsData.localChannels.map(ch => {
      // WAT-12562: logo and wordmark flag from ONE helper call, so they always describe the same asset.
      const logoFields = localLogoFields(ch);
      return {
        label: ch.network, // Use network as the label (e.g., "NBC", "FOX")
        displayText: ch.network, // Required for search component
        logo: logoFields?.logo ?? "", // icon, else logo (already transformed by the zipcode component)
        // WAT-12562: same wordmark-logo flag as the local cards, so a local opened from search gets
        // the white chip in its modal header.
        isOvLogo: logoFields?.isOvLogo,
        // [PR #8099 review] Carry the channel number, because it is the only thing that tells two
        // markets' stations of the same NETWORK apart here. `getChoiceKey`'s third rung is
        // `label|chnlNum` (the provider mints no matchId — see buildChannelObject in
        // @directv-digital/dds.apis.local-rsn-chnls, which sets `chnlNum: channel?.channelNumber`
        // and no identity field). Without it every local keyed `chnl:pbs|`, so after a ZIP change
        // the previous market's PBS matched the new market's PBS exactly and the reconciler kept the
        // STALE object — the WAT-11791 defect surviving for the one channel class that is actually
        // market-scoped. It also un-collides two same-named stations in one market, which the
        // expand branch needs to keep both.
        chnlNum: ch?.chnlNum ?? ch?.channelNumber,
        categoryId: "LOCAL",
        rawData: ch,
        isLocal: true,
        signaturePackages: [], // Required for clickability check
        genrePackages: [], // Required for clickability check
        hasIncludedPackages: true // Local channels are always included
      };
    });

    // Transform RSN channels to include displayText
    const rsnChannelsForSearch = localRsnChnlsData.rsnChannels.map(ch => {
      const logoFields = localLogoFields(ch);
      return {
        label: ch.network, // Use network as the label (e.g., "FanDuel Sports Network Detroit")
        displayText: ch.network, // Required for search component
        logo: logoFields?.logo ?? "", // icon, else logo (already transformed by the zipcode component)
        isOvLogo: logoFields?.isOvLogo,
        // Same reason as the local literal above — RSN carriage is DMA-scoped too.
        chnlNum: ch?.chnlNum ?? ch?.channelNumber,
        categoryId: "RSN",
        rawData: ch,
        isRsn: true,
        signaturePackages: [], // Required for clickability check
        genrePackages: [], // Required for clickability check
        hasIncludedPackages: Boolean(ch.packageNames?.length > 0) // RSN channels check packageNames
      };
    });

    // Combine all channels
    channels = [...channels, ...localChannelsForSearch, ...rsnChannelsForSearch];
  }

  return channels;
};
